import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { authApi } from '../api';
import { TOKEN_KEY } from '../api/client';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /** Restores the session on refresh from the stored token. */
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }

    authApi
      .me()
      .then(res => setUser(res.data.user))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setLoading(false));
  }, []);

  /** Only the super admin may hold a session in this panel. */
  const acceptSession = useCallback(payload => {
    if (payload.user.role !== 'superadmin') {
      throw new Error('This panel is restricted to Super Admin accounts');
    }
    localStorage.setItem(TOKEN_KEY, payload.token);
    setUser(payload.user);
    return payload.user;
  }, []);

  const loginWithPassword = useCallback(
    async (email, password) => acceptSession((await authApi.login(email, password)).data),
    [acceptSession],
  );

  const loginWithOtp = useCallback(
    async (mobile, otp) => acceptSession((await authApi.verifyOtp(mobile, otp)).data),
    [acceptSession],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      /* logging out locally matters more than the round-trip */
    }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, loginWithPassword, loginWithOtp, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};
